import React, {useMemo, useState} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";
import cx from "classnames";

import {useDishDetailsConfig} from "@components/DishDetails";
import {MenuCategoryItem} from "@components/Menu/interfaces";
import MenuItem from "@components/MenuItem";
import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton/TransparentButton";

import CategoryBannerImage from "../CategoryBannerImage/CategoryBannerImage";
import {getSizesAttribute} from "../utils/getSizesAttribute";
import styles from "./CategoryBanner0.scss";

interface CategoryBanner0Props {
  category: MenuCategoryItem;
  items: {
    [key: string]: DishDetailsItem;
  };
  menuItemList?: DishDetailsItem[];
  isInitiallyCollapsed: boolean;
}

export default function CategoryBanner0({
  category,
  items,
  menuItemList,
  isInitiallyCollapsed,
}: CategoryBanner0Props): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState<boolean | undefined>(
    isInitiallyCollapsed
  );
  const {forceTwoColumns, hasDateSelector} = useDishDetailsConfig();

  const isEverydayCategory = useMemo(
    () => category.name.slice(0, 8).toLowerCase() === "everyday",
    [category]
  );

  const sizesAttribute = useMemo(() => {
    if (category) {
      return getSizesAttribute({
        category,
        hasTwoColumns: forceTwoColumns,
        hasDateSelector,
      });
    }
  }, [category]);

  const onCollapse: () => void = () => {
    setIsCollapsed(!isCollapsed);
  };

  const itemWrapperClassName = useMemo(() => {
    const isTwoColumns = category.column_number === 2 || forceTwoColumns;
    return cx(styles.menuItemWrapper, {
      [styles.menuItemWidthMedium]: isTwoColumns,
      [styles.menuItemWidthSmall]: !isTwoColumns,
      [styles.collapsibleCategoryMediumScreen]:
        category.collapsible && isTwoColumns,
      [styles.collapsibleCategorySmallScreen]:
        category.collapsible && !isTwoColumns,
    });
  }, [category]);

  let categoryHeader;
  if (category.display_name) {
    const headerContent = (
      <>
        <h2
          style={{color: category.text_color || "inherit"}}
          className={styles.categoryHeading}
        >
          {category.display_name}
        </h2>
        {category.collapsible && (
          <span
            className={cx(styles.caretIcon, isCollapsed && styles.collapsed)}
          >
            <NomiconIcon icon="nomicon-caret-down" />
          </span>
        )}
      </>
    );
    const commonAttributes = {
      "data-testid": `collapse-category-${category.id}`,
      className: cx(
        isEverydayCategory && styles.everyday,
        styles.categoryHeader
      ),
    };
    if (category.collapsible) {
      categoryHeader = (
        <TransparentButton onClick={onCollapse} {...commonAttributes}>
          {headerContent}
        </TransparentButton>
      );
    } else {
      categoryHeader = <div {...commonAttributes}>{headerContent}</div>;
    }
  }

  return (
    <>
      {categoryHeader}
      {(!category.collapsible || !isCollapsed) && (
        <>
          {category.banner && <CategoryBannerImage category={category} />}
          {category.description && (
            <div
              data-testid={`category-description-${category.id}`}
              className={styles.categoryDescription}
              dangerouslySetInnerHTML={{__html: category.description}}
            ></div>
          )}
          <section className={styles.menuItemsSection}>
            {category.item_ids.map((itemId: number) => {
              return (
                <MenuItem
                  sizesAttribute={sizesAttribute}
                  key={`menu-item-${itemId}`}
                  item={items[itemId]}
                  menuItemList={menuItemList}
                  wrapperClassName={itemWrapperClassName}
                  socialProofClassName={styles.itemSocialProof}
                />
              );
            })}
          </section>
        </>
      )}
    </>
  );
}
